import React, { useRef, useState } from "react";
import "../Style/Contact.css";
import emailjs from "@emailjs/browser";
import {
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaClock,
  FaPaperPlane,
  FaCheckCircle,
  FaGlobeAsia,
  FaShieldAlt
} from "react-icons/fa";

function Contact() {
  const formRef = useRef();
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [errors, setErrors] = useState({});

  const dropdownData = {
    "iso-core": {
      heading: "ISO Management Systems",
      teaser: "International core operational management frameworks commonly deployed across organizations.",
      links: [
        { label: "ISO 9001:2015 Quality Management System (QMS)", id: "iso-9001" },
        { label: "ISO 14001:2015 Environmental Management System (EMS)", id: "iso-14001" },
        { label: "ISO 45001:2018 Occupational Health & Safety Management System", id: "iso-45001" },
        { label: "ISO 50001:2018 Energy Management System", id: "iso-50001" },
        { label: "ISO 55001:2014 Asset Management System", id: "iso-55001" },
        { label: "ISO 41001:2018 Facility Management System", id: "iso-41001" }
      ]
    },
    "food-lab": {
      heading: "Food Safety, Healthcare & IT Standards",
      teaser: "Targeted structural security controls handling nutrition logistics, medical devices, and IT hardware.",
      links: [
        { label: "ISO 22000:2018 Food Safety Management System", id: "iso-22000" },
        { label: "HACCP Certification", id: "haccp" },
        { label: "FSSC 22000 Food Safety Certification", id: "fssc-22000" },
        { label: "GMP (Good Manufacturing Practices)", id: "gmp" },
        { label: "GHP (Good Hygiene Practices)", id: "ghp" },
        { label: "BRCGS Food Safety Certification", id: "brcgs" },
        { label: "MSC Chain of Custody (Sustainable Seafood)", id: "msc-coc" },
        { label: "Halal Certification", id: "halal" },
        { label: "ISO 13485:2016 Medical Devices QMS", id: "iso-13485" },
        { label: "ISO 15189 Medical Laboratories Requirements", id: "iso-15189" },
        { label: "NABH Consultancy Services", id: "nabh-consultancy" },
        { label: "ISO 20000-1:2018 IT Service Management", id: "iso-20000-1" }
      ]
    },
    infosec: {
      heading: "InfoSec & Infrastructure Security",
      teaser: "Advanced information shielding parameters protecting critical corporate and healthcare networks.",
      links: [
        { label: "ISO/IEC 27001:2022 Information Security Management (ISMS)", id: "iso-27001" },
        { label: "ISO 28000:2022 Supply Chain Security Management", id: "iso-28000" }
      ]
    },
    regulatory: {
      heading: "Corporate Governance, Risk & Operations",
      teaser: "Enterprise guidelines maintaining legal compliance, continuity, risk management, and responsibility.",
      links: [
        { label: "ISO 22301:2019 Business Continuity Management System", id: "iso-22301" },
        { label: "ISO 37001:2016 Anti-Bribery Management System", id: "iso-37001" },
        { label: "ISO 31000 Risk Management Guidelines", id: "iso-31000" },
        { label: "ISO 21001:2018 Educational Organization Management", id: "iso-21001" },
        { label: "ISO 39001:2012 Road Traffic Safety Management", id: "iso-39001" },
        { label: "ISO 26000 Social Responsibility Guidance", id: "iso-26000" },
        { label: "ISO 20121:2024 Sustainable Event Management", id: "iso-20121" },
        { label: "ISO 10002:2018 Customer Complaint Management", id: "iso-10002" },
        { label: "ISO 10001:2018 Customer Satisfaction Codes of Conduct", id: "iso-10001" }
      ]
    }
  };

  const availableStandards = selectedCategory ? dropdownData[selectedCategory]?.links || [] : [];

  const validateForm = (formData) => {
    const newErrors = {};

    const userName = formData.get("user_name")?.trim();
    const userEmail = formData.get("user_email")?.trim();
    const userPhone = formData.get("user_phone")?.trim();
    const country = formData.get("country")?.trim();
    const companyName = formData.get("company_name")?.trim();
    const serviceCategory = formData.get("service_category")?.trim();
    const certificateStandard = formData.get("certificate_standard")?.trim();
    const message = formData.get("message")?.trim();

    const nameRegex = /^[A-Za-zÀ-ÿ\s.'-]{2,50}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9+\-\s()]{7,20}$/;
    const countryRegex = /^[A-Za-zÀ-ÿ\s.'-]{2,50}$/;
    const companyRegex = /^[A-Za-z0-9À-ÿ\s.&,'-]{2,80}$/;

    if (!userName) {
      newErrors.user_name = "Please enter your full name.";
    } else if (!nameRegex.test(userName)) {
      newErrors.user_name = "Enter a valid name.";
    }

    if (!userEmail) {
      newErrors.user_email = "Please enter your email address.";
    } else if (!emailRegex.test(userEmail)) {
      newErrors.user_email = "Please enter a valid email address.";
    }

    if (!userPhone) {
      newErrors.user_phone = "Please enter your phone number.";
    } else if (!phoneRegex.test(userPhone)) {
      newErrors.user_phone = "Please enter a valid phone number.";
    }

    if (!country) {
      newErrors.country = "Please enter your country.";
    } else if (!countryRegex.test(country)) {
      newErrors.country = "Enter a valid country name.";
    }

    if (!companyName) {
      newErrors.company_name = "Please enter your company name.";
    } else if (!companyRegex.test(companyName)) {
      newErrors.company_name = "Enter a valid company name.";
    }

    if (!serviceCategory) newErrors.service_category = "Please select a service category.";
    if (!certificateStandard) newErrors.certificate_standard = "Please select a certificate standard.";
    if (!message) {
      newErrors.message = "Please describe your requirements.";
    } else if (message.length < 10) {
      newErrors.message = "Requirements must be at least 10 characters.";
    }

    return newErrors;
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setStatusMsg("");
    setErrors({});

    const formData = new FormData(formRef.current);
    const validationErrors = validateForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setStatusMsg("Please fix the highlighted fields.");
      return;
    }

    setIsSending(true);

    
    try {
      await emailjs.sendForm(
        "service_q53rq8z",
        "template_y9vaghf",
        formRef.current,
        "UmuEsEW-ZDPBHdt3X"
      );

      setStatusMsg("Message sent successfully!");
      formRef.current.reset();
      setSelectedCategory("");
    } catch (error) {
      console.error("EmailJS error:", error);
      setStatusMsg("Failed to send message. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="split-contact-page">
      <div className="split-left-pane">
        <div className="split-brand-header">
          <div className="brand-logo-badge">AI</div>
          <span className="brand-title">Ascent Inspecta Maldives</span>
        </div>

        <div className="split-hero-card">
          <h1>Let’s talk about your ISO certification needs.</h1>
          <p className="split-lead-text">
            Reach out to our team for certification guidance, audit preparation, documentation support, and a tailored quotation.
          </p>

          <div className="contact-details-box">
            <div className="contact-info-block">
              <FaMapMarkerAlt className="info-icon" />
              <div>
                <span className="info-label">Office Location</span>
                <p className="info-value">Minhaa, Karankaa Magu, Malé, Republic of Maldives</p>
              </div>
            </div>

            <div className="contact-info-block">
              <FaClock className="info-icon" />
              <div>
                <span className="info-label">Working Hours & Timings</span>
                <p className="info-value">Saturday – Thursday: 9:00 AM – 6:00 PM</p>
                <span className="info-subtext">Friday: Closed / By Appointment Only</span>
              </div>
            </div>

            <div className="contact-info-block">
              <FaPhoneAlt className="info-icon" />
              <div>
                <span className="info-label">Direct Contact</span>
                <p className="info-value">+91 9867180395 | info@ascentinspecta.com</p>
              </div>
            </div>
          </div>

          <div className="split-trust-list" style={{ marginTop: "20px" }}>
            <div><FaCheckCircle /> Quick response</div>
            <div><FaShieldAlt /> Trusted support</div>
            <div><FaGlobeAsia /> Multi-standard expertise</div>
          </div>
        </div>

        <div className="split-bottom-bar">
          <span>&copy; {new Date().getFullYear()} Ascent Inspecta. All Rights Reserved.</span>
        </div>
      </div>

      <div className="split-right-pane">
        <nav className="split-top-nav">
          <a href="#contact" className="nav-contact-btn">Contact Us</a>
        </nav>

        <div className="split-form-container">
          <span className="section-tag tag-red">SEND A MESSAGE</span>
          <h2>Request a consultation or quote</h2>
          <p className="form-subtitle-text">
            Fill out the form below and our team will get back to you with the right ISO guidance.
          </p>

          <form ref={formRef} className="split-interactive-form" onSubmit={sendEmail} noValidate>
            <input type="hidden" name="time" value={new Date().toLocaleString()} />

            <div className="split-row-inputs">
              <div className="split-input-box">
                <label>Full Name</label>
                <input
                  type="text"
                  name="user_name"
                  placeholder="Full Name"
                  required
                  pattern="^[A-Za-zÀ-ÿ\s.'-]{2,50}$"
                  title="Enter a valid name using letters only."
                />
                {errors.user_name && <span className="field-error">{errors.user_name}</span>}
              </div>

              <div className="split-input-box">
                <label>Email Address</label>
                <input
                  type="email"
                  name="user_email"
                  placeholder="Email Address"
                  required
                  pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                  title="Enter a valid email address."
                />
                {errors.user_email && <span className="field-error">{errors.user_email}</span>}
              </div>
            </div>

            <div className="split-row-inputs">
              <div className="split-input-box">
                <label>Phone Number</label>
                <input
                  type="text"
                  name="user_phone"
                  placeholder="Phone Number"
                  required
                  pattern="^[0-9+\-\s()]{7,20}$"
                  title="Enter a valid phone number."
                />
                {errors.user_phone && <span className="field-error">{errors.user_phone}</span>}
              </div>

              <div className="split-input-box">
                <label>Country</label>
                <input
                  type="text"
                  name="country"
                  placeholder="Country"
                  required
                  pattern="^[A-Za-zÀ-ÿ\s.'-]{2,50}$"
                  title="Enter a valid country name."
                />
                {errors.country && <span className="field-error">{errors.country}</span>}
              </div>
            </div>

            <div className="split-row-inputs">
              <div className="split-input-box">
                <label>Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  placeholder="Company Name"
                  required
                  pattern="^[A-Za-z0-9À-ÿ\s.&,'-]{2,80}$"
                  title="Enter a valid company name."
                />
                {errors.company_name && <span className="field-error">{errors.company_name}</span>}
              </div>

              <div className="split-input-box">
                <label>Select Service Category</label>
                <select
                  name="service_category"
                  required
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Choose Service Category</option>
                  {Object.entries(dropdownData).map(([categoryKey, categoryObj]) => (
                    <option key={categoryKey} value={categoryKey}>
                      {categoryObj.heading}
                    </option>
                  ))}
                </select>
                {errors.service_category && <span className="field-error">{errors.service_category}</span>}
              </div>
            </div>

            <div className="split-row-inputs">
              <div className="split-input-box">
                <label>Certificate Standard</label>
                <select name="certificate_standard" required disabled={!selectedCategory}>
                  <option value="">
                    {selectedCategory ? "Choose Certificate Standard" : "Please select a category first"}
                  </option>
                  {availableStandards.map((link) => (
                    <option key={link.id} value={link.label}>
                      {link.label}
                    </option>
                  ))}
                </select>
                {errors.certificate_standard && <span className="field-error">{errors.certificate_standard}</span>}
              </div>
            </div>

            <div className="split-input-box full-width">
              <label>Requirements</label>
              <textarea
                name="message"
                rows="3"
                placeholder="Tell us about your requirements"
                required
              ></textarea>
              {errors.message && <span className="field-error">{errors.message}</span>}
            </div>

            {statusMsg && <p className="form-status-message">{statusMsg}</p>}

            <button type="submit" className="split-submit-btn" disabled={isSending}>
              <FaPaperPlane /> {isSending ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>

        <div className="split-right-footer-nav">
          <span>info@ascentinspecta.com</span>
          <span>•</span>
          <span>+91 9867180395</span>
          <span>•</span>
          <span>Malé, Maldives</span>
        </div>
      </div>
    </div>
  );
}

export default Contact;